import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Toolbar,
  AppBar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Snackbar,
  RadioGroup,
  FormControlLabel,
  Radio,
  OutlinedInput,
  Checkbox,
  ListItemText,
  TextField,
  FormHelperText,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useUser } from '../hooks';
import {
  DrawerNavigation,
  DetIcon,
  PaginationBar,
  DRAWER_WIDTH,
  COLLAPSED_WIDTH,
  SendToTaskPanel,
  ConfirmDialog,
} from '../components';
import DetectionCard from '../components/DetectionCard';
import DetectionObjectEditor from '../components/DetectionObjectEditor';
import { deleteDetections } from '../services/detectionService';
import {
  PageName,
  ApiResponse,
  Detection,
  DetectionDataset,
  DetectionFlag,
  DetectionSearchPayload,
  DetectionSearchResponse,
  DetectionObject,
  TaskSummary,
} from '../types';
import api from '../services/api';
import { IMAGE_BASE_URLS } from '../utils/imageUrl';
import { decodeDetectionParams, encodeDetectionParams, hasURLParams, clearURLParams } from '../utils/urlParams';
import { getTaskBgColor } from '../constants/taskConfig';

// Constants
const THEME_COLOR = '#9c27b0';
const IMAGE_BASE_URL = IMAGE_BASE_URLS.detection;

// Patch payload type for detection updates
interface DetectionPatchPayload {
  dataset?: { id: string };
  flags?: { id: string }[];
}
const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];
const IMAGE_WIDTH = 320;
const IMAGE_HEIGHT = 240;

interface DetectionSearchProps {
  onLogout: () => void;
  onNavigate: (page: PageName) => void;
}

function DetectionSearch({ onLogout, onNavigate }: DetectionSearchProps) {
  const theme = useTheme();
  // UI state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const { user, loading: userLoading } = useUser();

  // Dropdown data
  const [datasets, setDatasets] = useState<DetectionDataset[]>([]);
  const [flags, setFlags] = useState<DetectionFlag[]>([]);
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [loadingTasks, setLoadingTasks] = useState<boolean>(true);

  // Search filters
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);
  const [flagsMustHave, setFlagsMustHave] = useState<string[]>([]);
  const [flagsAtLeastOne, setFlagsAtLeastOne] = useState<string[]>([]);
  const [flagsDontHave, setFlagsDontHave] = useState<string[]>([]);
  const [descriptionFilter, setDescriptionFilter] = useState<string>('');
  const [idFilter, setIdFilter] = useState<string>('');
  const [sorting, setSorting] = useState<'normal' | 'reverse' | 'random'>('reverse');
  const [pageSize, setPageSize] = useState<number>(20);
  const [page, setPage] = useState<number>(1);

  // Results
  const [results, setResults] = useState<Detection[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  // Selection
  const [selectedDetections, setSelectedDetections] = useState<string[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  // Bulk edit
  const [bulkDataset, setBulkDataset] = useState<string>('');
  const [bulkFlags, setBulkFlags] = useState<string[]>([]);

  // Object editor
  const [editingDetection, setEditingDetection] = useState<Detection | null>(null);
  const [objectEditorOpen, setObjectEditorOpen] = useState<boolean>(false);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Loading states
  const [loading, setLoading] = useState<boolean>(true);
  const [searching, setSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Refs
  const hasFetchedInitialData = useRef(false);
  const hasAppliedURLParams = useRef(false);

  // Ref to store the latest handleSearch
  const handleSearchRef = useRef<(page?: number) => Promise<void>>(undefined);

  const currentDrawerWidth = drawerCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  // Computed values
  const selectedDetectionsSet = useMemo(() => new Set(selectedDetections), [selectedDetections]);
  const totalPages = Math.ceil(totalResults / pageSize);

  // Filter out hidden items
  const visibleResults = useMemo(() =>
    results.filter(d => !hiddenIds.has(d.id)),
    [results, hiddenIds]
  );

  // Sorted dropdown data
  const sortedDatasets = useMemo(() => {
    return [...datasets].sort((a, b) => a.label.localeCompare(b.label));
  }, [datasets]);

  const sortedFlags = useMemo(() => {
    return [...flags].sort((a, b) => a.label.localeCompare(b.label));
  }, [flags]);

  // Check if user is admin
  const isAdmin = useMemo(() => user?.roles?.includes('admin') || false, [user]);

  // Fetch initial dropdown data
  useEffect(() => {
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;

    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [datasetsRes, flagsRes, tasksRes] = await Promise.all([
          api.get<ApiResponse<DetectionDataset[]>>('datasets/list/detection'),
          api.get<ApiResponse<DetectionFlag[]>>('detection/list/flags'),
          api.get<ApiResponse<TaskSummary[]>>('tasks/list'),
        ]);

        setDatasets(datasetsRes.data.results || []);
        setFlags(flagsRes.data.results || []);

        // Filter tasks to only show detection type
        const fetchedTasks = (tasksRes.data.results || [])
          .filter((task: TaskSummary) => task.type === 'detection')
          .sort((a: TaskSummary, b: TaskSummary) => a.title.localeCompare(b.title));
        setTasks(fetchedTasks);
        setLoadingTasks(false);
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
        setError('Failed to load dropdown data. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Apply URL params after initial data is loaded
  useEffect(() => {
    if (loading || hasAppliedURLParams.current || !hasURLParams()) return;
    hasAppliedURLParams.current = true;

    const params = decodeDetectionParams();

    // Apply params to state
    if (params.datasets && params.datasets.length > 0) setSelectedDatasets(params.datasets);
    if (params.mustHave && params.mustHave.length > 0) setFlagsMustHave(params.mustHave);
    if (params.atLeastOne && params.atLeastOne.length > 0) setFlagsAtLeastOne(params.atLeastOne);
    if (params.dontHave && params.dontHave.length > 0) setFlagsDontHave(params.dontHave);
    if (params.desc) setDescriptionFilter(params.desc);
    if (params.id) setIdFilter(params.id);
    if (params.sort) setSorting(params.sort as 'normal' | 'reverse' | 'random');
    if (params.pageSize) setPageSize(params.pageSize);
    if (params.page) setPage(params.page);

    // Auto-search with URL params
    setTimeout(() => {
      handleSearchRef.current?.(params.page || 1);
    }, 0);
  }, [loading]);

  // Search handler
  const handleSearch = useCallback(async (searchPage: number = 1) => {
    setSearching(true);
    setError(null);
    setHiddenIds(new Set());

    try {
      const payload: DetectionSearchPayload = {
        page: searchPage,
        pageSize,
        sorting,
      };

      if (selectedDatasets.length > 0) payload.datasets = selectedDatasets;
      payload.flags = {
        mustHave: flagsMustHave,
        atLeastOne: flagsAtLeastOne,
        dontHave: flagsDontHave,
      };
      if (descriptionFilter.trim()) payload.description = descriptionFilter.trim();
      if (idFilter.trim()) payload.id = idFilter.trim();

      const response = await api.post<ApiResponse<DetectionSearchResponse>>('detection/search', payload);
      const data = response.data.results;

      setResults(data.items || []);
      setTotalResults(data.totalResults || 0);
      setPage(data.page || 1);
      setHasSearched(true);
      setSelectedDetections([]);

      // Update URL with search params
      encodeDetectionParams({
        datasets: selectedDatasets,
        mustHave: flagsMustHave,
        atLeastOne: flagsAtLeastOne,
        dontHave: flagsDontHave,
        desc: descriptionFilter.trim() || undefined,
        id: idFilter.trim() || undefined,
        sort: sorting,
        pageSize: pageSize,
        page: searchPage,
      });
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  }, [pageSize, sorting, selectedDatasets, flagsMustHave, flagsAtLeastOne, flagsDontHave, descriptionFilter, idFilter]);

  // Update handleSearchRef with latest handleSearch
  handleSearchRef.current = handleSearch;

  // Reset handler
  const handleReset = useCallback(() => {
    setSelectedDatasets([]);
    setFlagsMustHave([]);
    setFlagsAtLeastOne([]);
    setFlagsDontHave([]);
    setDescriptionFilter('');
    setIdFilter('');
    setSorting('normal');
    setPageSize(20);
    setPage(1);
    setResults([]);
    setTotalResults(0);
    setHasSearched(false);
    setSelectedDetections([]);
    setBulkDataset('');
    setBulkFlags([]);
    clearURLParams();
  }, []);

  // Pagination handlers
  const handlePrevious = useCallback(() => {
    if (page > 1) handleSearch(page - 1);
  }, [page, handleSearch]);

  const handleNext = useCallback(() => {
    if (page < totalPages) handleSearch(page + 1);
  }, [page, totalPages, handleSearch]);

  const handlePageChange = useCallback((newPage: number) => {
    handleSearch(newPage);
  }, [handleSearch]);

  // Selection handlers
  const handleToggleSelection = useCallback((id: string) => {
    setSelectedDetections((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  }, []);

  const handleHideItem = useCallback((id: string) => {
    setHiddenIds(prev => new Set(prev).add(id));
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedDetections(visibleResults.map((d) => d.id));
  }, [visibleResults]);

  const handleClearSelection = useCallback(() => {
    setSelectedDetections([]);
  }, []);

  // Field change handler for individual detection
  const handleDetectionFieldChange = useCallback(
    async (detectionId: string, field: 'dataset' | 'flags', value: string | string[]) => {
      try {
        const patch: DetectionPatchPayload = {};
        // Backend expects objects with 'id' field - onlyIds() extracts them
        if (field === 'dataset') {
          patch.dataset = { id: value as string };
        } else if (field === 'flags') {
          patch.flags = (value as string[]).map((id) => ({ id }));
        }

        await api.patch(`detection/patch/${detectionId}`, patch);

        // Update local state
        setResults((prev) =>
          prev.map((d) => {
            if (d.id !== detectionId) return d;
            if (field === 'dataset') {
              const dataset = datasets.find((ds) => ds.id === value);
              return dataset ? { ...d, dataset } : d;
            } else if (field === 'flags') {
              const newFlags = (value as string[])
                .map((id) => flags.find((f) => f.id === id))
                .filter((f): f is DetectionFlag => f !== undefined);
              return { ...d, flags: newFlags };
            }
            return d;
          })
        );

        setSnackbar({ open: true, message: `${field === 'dataset' ? 'Dataset' : 'Flags'} updated`, severity: 'success' });
      } catch (err) {
        console.error(`Failed to update ${field}:`, err);
        setSnackbar({ open: true, message: `Failed to update ${field}`, severity: 'error' });
      }
    },
    [datasets, flags]
  );

  // Bulk apply handler
  const handleBulkApply = useCallback(async () => {
    if (selectedDetections.length === 0) return;

    const hasBulkDataset = bulkDataset !== '';
    const hasBulkFlags = bulkFlags.length > 0;

    if (!hasBulkDataset && !hasBulkFlags) {
      setSnackbar({ open: true, message: 'No changes to apply', severity: 'error' });
      return;
    }

    setSearching(true);

    try {
      const apiResults = await Promise.allSettled(
        selectedDetections.map((detectionId) => {
          const patch: DetectionPatchPayload = {};
          // Backend expects objects with 'id' field - onlyIds() extracts them
          if (hasBulkDataset) patch.dataset = { id: bulkDataset };
          if (hasBulkFlags) patch.flags = bulkFlags.map((id) => ({ id }));
          return api.patch(`detection/patch/${detectionId}`, patch);
        })
      );

      // Count successes and log failures
      let successCount = 0;
      let failCount = 0;
      apiResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successCount++;
        } else {
          console.error(`Failed to update detection ${selectedDetections[index]}:`, result.reason);
          failCount++;
        }
      });

      // Update local state for successful updates
      if (successCount > 0) {
        setResults((prev) =>
          prev.map((d) => {
            if (!selectedDetections.includes(d.id)) return d;
            let updated = { ...d };
            if (hasBulkDataset) {
              const dataset = datasets.find((ds) => ds.id === bulkDataset);
              if (dataset) updated.dataset = dataset;
            }
            if (hasBulkFlags) {
              const newFlags = bulkFlags
                .map((id) => flags.find((f) => f.id === id))
                .filter((f): f is DetectionFlag => f !== undefined);
              updated.flags = newFlags;
            }
            return updated;
          })
        );
      }

      // Reset bulk fields
      setBulkDataset('');
      setBulkFlags([]);
      setSelectedDetections([]);

      if (failCount > 0) {
        setSnackbar({
          open: true,
          message: `Updated ${successCount}, failed ${failCount}`,
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: `Updated ${successCount} detection${successCount > 1 ? 's' : ''}`,
          severity: 'success',
        });
      }
    } catch (err) {
      console.error('Bulk apply failed:', err);
      setSnackbar({ open: true, message: 'Bulk update failed', severity: 'error' });
    } finally {
      setSearching(false);
    }
  }, [selectedDetections, bulkDataset, bulkFlags, datasets, flags]);

  // Bulk delete handler - opens confirmation dialog
  const handleBulkDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  // Confirm delete handler - executes bulk delete
  const handleConfirmDelete = useCallback(async () => {
    setDeleting(true);
    try {
      const { succeeded, failed } = await deleteDetections(selectedDetections);
      if (succeeded.length > 0) {
        const succeededSet = new Set(succeeded);
        setResults((prev) => prev.filter((d) => !succeededSet.has(d.id)));
        setTotalResults((prev) => prev - succeeded.length);
        setSelectedDetections((prev) => prev.filter((id) => !succeededSet.has(id)));
      }
      if (failed.length > 0) {
        setSnackbar({
          open: true,
          message: `Deleted ${succeeded.length}, failed ${failed.length}`,
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: `Deleted ${succeeded.length} detection(s)`,
          severity: 'success',
        });
      }
    } catch (err) {
      console.error('Delete failed:', err);
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  }, [selectedDetections]);

  // Image click handler - open object editor
  const handleImageClick = useCallback((detection: Detection) => {
    setEditingDetection(detection);
    setObjectEditorOpen(true);
  }, []);

  // Object editor close handler
  const handleObjectEditorClose = useCallback(() => {
    setObjectEditorOpen(false);
    setEditingDetection(null);
  }, []);

  // Objects updated handler
  const handleObjectsUpdated = useCallback((detectionId: string, objects: DetectionObject[]) => {
    setResults((prev) =>
      prev.map((d) => (d.id === detectionId ? { ...d, objects } : d))
    );
    setEditingDetection((prev) =>
      prev && prev.id === detectionId ? { ...prev, objects } : prev
    );
  }, []);

  // Press F in editor to close and set "verified" flag
  const handleEditorFinish = useCallback(() => {
    if (!editingDetection) return;
    const verifiedFlag = flags.find(f => f.label.toLowerCase() === 'verified');
    if (!verifiedFlag) return;
    const hasVerified = editingDetection.flags.some(f => f.id === verifiedFlag.id);
    if (hasVerified) return;
    const newFlags = [...editingDetection.flags.map(f => f.id), verifiedFlag.id];
    handleDetectionFieldChange(editingDetection.id, 'flags', newFlags);
  }, [editingDetection, flags, handleDetectionFieldChange]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  if (userLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: THEME_COLOR,
          transition: 'width 0.2s, margin-left 0.2s',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <DetIcon size={28} />
            <Typography variant="h6" noWrap>
              Detection Search
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {user && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, mr: 2 }}>
                <Typography variant="body2">@{user.username}</Typography>
                {user.roles?.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                ))}
              </Box>
            )}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={onLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <DrawerNavigation
        currentPage="searchDet"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: 'background.default',
          transition: 'width 0.2s, margin-left 0.2s',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" disableGutters>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Search Form */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: getTaskBgColor('detection', theme.palette.mode),
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <DetIcon size={40} />
              </Box>
              <Box>
                <Typography variant="h5">Detection Search</Typography>
                <Typography variant="body2" color="text.secondary">
                  Search and edit detection annotations
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {/* Flags - Must Have */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Must Have</InputLabel>
                  <Select<string[]>
                    multiple
                    value={flagsMustHave}
                    onChange={(e) => setFlagsMustHave(e.target.value as string[])}
                    input={<OutlinedInput label="Must Have" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((flagId) => {
                          const flag = flags.find((f) => f.id === flagId);
                          return (
                            <Chip
                              key={flagId}
                              label={flag?.label || flagId}
                              size="small"
                              color="success"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {sortedFlags.map((flag) => (
                      <MenuItem
                        key={flag.id}
                        value={flag.id}
                        disabled={flagsAtLeastOne.includes(flag.id) || flagsDontHave.includes(flag.id)}
                      >
                        {flag.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Must have ALL</FormHelperText>
                </FormControl>
              </Grid>

              {/* Flags - At Least One */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>At Least One</InputLabel>
                  <Select<string[]>
                    multiple
                    value={flagsAtLeastOne}
                    onChange={(e) => setFlagsAtLeastOne(e.target.value as string[])}
                    input={<OutlinedInput label="At Least One" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((flagId) => {
                          const flag = flags.find((f) => f.id === flagId);
                          return (
                            <Chip
                              key={flagId}
                              label={flag?.label || flagId}
                              size="small"
                              color="info"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {sortedFlags.map((flag) => (
                      <MenuItem
                        key={flag.id}
                        value={flag.id}
                        disabled={flagsMustHave.includes(flag.id) || flagsDontHave.includes(flag.id)}
                      >
                        {flag.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>At least ONE</FormHelperText>
                </FormControl>
              </Grid>

              {/* Flags - Don't Have */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Don't Have</InputLabel>
                  <Select<string[]>
                    multiple
                    value={flagsDontHave}
                    onChange={(e) => setFlagsDontHave(e.target.value as string[])}
                    input={<OutlinedInput label="Don't Have" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((flagId) => {
                          const flag = flags.find((f) => f.id === flagId);
                          return (
                            <Chip
                              key={flagId}
                              label={flag?.label || flagId}
                              size="small"
                              color="error"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {sortedFlags.map((flag) => (
                      <MenuItem
                        key={flag.id}
                        value={flag.id}
                        disabled={flagsMustHave.includes(flag.id) || flagsAtLeastOne.includes(flag.id)}
                      >
                        {flag.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Must NOT have</FormHelperText>
                </FormControl>
              </Grid>

              {/* Datasets */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Datasets</InputLabel>
                  <Select<string[]>
                    multiple
                    value={selectedDatasets}
                    onChange={(e) => setSelectedDatasets(e.target.value as string[])}
                    input={<OutlinedInput label="Datasets" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((datasetId) => {
                          const dataset = datasets.find((d) => d.id === datasetId);
                          return (
                            <Chip
                              key={datasetId}
                              label={dataset?.label || datasetId}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {sortedDatasets.map((dataset) => (
                      <MenuItem key={dataset.id} value={dataset.id}>
                        <Checkbox checked={selectedDatasets.includes(dataset.id)} />
                        <ListItemText primary={dataset.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Description */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  placeholder="Search by description..."
                  helperText="Case insensitive substring search"
                  value={descriptionFilter}
                  onChange={(e) => setDescriptionFilter(e.target.value)}
                />
              </Grid>

              {/* ID */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="ID"
                  placeholder="MongoDB ObjectId"
                  helperText=" "
                  value={idFilter}
                  onChange={(e) => setIdFilter(e.target.value)}
                />
              </Grid>
            </Grid>

            {/* Page Size, Sorting and Buttons */}
            <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Page Size</InputLabel>
                  <Select
                    value={pageSize}
                    onChange={(e) => setPageSize(e.target.value as number)}
                    label="Page Size"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2">Sorting:</Typography>
                  <RadioGroup
                    row
                    value={sorting}
                    onChange={(e) => setSorting(e.target.value as 'normal' | 'reverse' | 'random')}
                  >
                    <FormControlLabel value="normal" control={<Radio size="small" />} label="Normal" />
                    <FormControlLabel value="reverse" control={<Radio size="small" />} label="Reverse" />
                    <FormControlLabel value="random" control={<Radio size="small" />} label="Random" />
                  </RadioGroup>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleReset}
                    disabled={searching}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={() => handleSearch(1)}
                    disabled={searching}
                    sx={{ bgcolor: THEME_COLOR, '&:hover': { bgcolor: '#7b1fa2' } }}
                  >
                    Search
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Results */}
          {hasSearched && (
            <>
              {/* Pagination Top */}
              <PaginationBar
                currentPage={page}
                totalPages={totalPages}
                totalResults={totalResults}
                disabled={searching}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onPageChange={handlePageChange}
              />

              {/* Selection & Bulk Edit Panel */}
              {results.length > 0 && (
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: getTaskBgColor('detection', theme.palette.mode) }}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Selection Controls */}
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button size="small" onClick={handleSelectAll} disabled={searching}>
                          Select All
                        </Button>
                        <Button size="small" onClick={handleClearSelection} disabled={searching}>
                          Clear
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Selected: {selectedDetections.length}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Bulk Dataset */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small" disabled={selectedDetections.length === 0}>
                        <InputLabel>Set Dataset</InputLabel>
                        <Select
                          value={bulkDataset}
                          onChange={(e) => setBulkDataset(e.target.value)}
                          label="Set Dataset"
                        >
                          <MenuItem value="">
                            <em>-- No change --</em>
                          </MenuItem>
                          {sortedDatasets.map((dataset) => (
                            <MenuItem key={dataset.id} value={dataset.id}>
                              {dataset.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Bulk Flags */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small" disabled={selectedDetections.length === 0}>
                        <InputLabel>Set Flags</InputLabel>
                        <Select
                          multiple
                          value={bulkFlags}
                          onChange={(e) => setBulkFlags(e.target.value as string[])}
                          input={<OutlinedInput label="Set Flags" />}
                          renderValue={(selected) =>
                            selected
                              .map((id) => flags.find((f) => f.id === id)?.label || id)
                              .join(', ')
                          }
                        >
                          {sortedFlags.map((flag) => (
                            <MenuItem key={flag.id} value={flag.id}>
                              <Checkbox checked={bulkFlags.includes(flag.id)} />
                              <ListItemText primary={flag.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Apply Button */}
                    <Grid size={{ xs: 12, md: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleBulkApply}
                          disabled={selectedDetections.length === 0 || searching}
                          sx={{ bgcolor: THEME_COLOR, '&:hover': { bgcolor: '#7b1fa2' } }}
                        >
                          Apply
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={handleBulkDeleteClick}
                            disabled={selectedDetections.length === 0 || deleting}
                          >
                            Delete
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Send to Task Panel */}
              {results.length > 0 && (
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: getTaskBgColor('detection', theme.palette.mode) }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Send to task:</Typography>
                    <SendToTaskPanel
                      tasks={tasks}
                      loadingTasks={loadingTasks}
                      selectedIds={selectedDetections}
                      allResultIds={results.map((d) => d.id)}
                      themeColor={THEME_COLOR}
                      onSuccess={(count) => setSnackbar({ open: true, message: `Sent ${count} sample(s) to task`, severity: 'success' })}
                      onError={(msg) => setSnackbar({ open: true, message: msg, severity: 'error' })}
                    />
                  </Box>
                </Paper>
              )}

              {/* Results Grid */}
              {visibleResults.length > 0 ? (
                <Grid container spacing={2}>
                  {visibleResults.map((detection) => (
                    <Grid key={detection.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                      <DetectionCard
                        detection={detection}
                        imageBaseUrl={IMAGE_BASE_URL}
                        imageWidth={IMAGE_WIDTH}
                        imageHeight={IMAGE_HEIGHT}
                        isSelected={selectedDetectionsSet.has(detection.id)}
                        datasets={sortedDatasets}
                        flags={sortedFlags}
                        onToggleSelection={handleToggleSelection}
                        onImageClick={handleImageClick}
                        onFieldChange={handleDetectionFieldChange}
                        onHide={handleHideItem}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No results found. Try adjusting your search criteria.
                  </Typography>
                </Paper>
              )}

              {/* Pagination Bottom */}
              {results.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <PaginationBar
                    currentPage={page}
                    totalPages={totalPages}
                    totalResults={totalResults}
                    disabled={searching}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    onPageChange={handlePageChange}
                  />
                </Box>
              )}
            </>
          )}

          {/* Initial State */}
          {!hasSearched && !searching && (
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: getTaskBgColor('detection', theme.palette.mode),
                  display: 'inline-flex',
                  mb: 2,
                }}
              >
                <DetIcon size={64} />
              </Box>
              <Typography variant="h6" gutterBottom>
                Search for Detection Samples
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the filters above to search for detections by dataset and flags.
                Click the Search button to view results.
              </Typography>
            </Paper>
          )}

          {/* Loading Overlay */}
          {searching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: THEME_COLOR }} />
            </Box>
          )}
        </Container>
      </Box>

      {/* Object Editor Dialog */}
      {editingDetection && (
        <DetectionObjectEditor
          open={objectEditorOpen}
          detection={editingDetection}
          imageUrl={`${IMAGE_BASE_URL}/${editingDetection.id}`}
          onClose={handleObjectEditorClose}
          onObjectsUpdated={handleObjectsUpdated}
          onFinish={handleEditorFinish}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete ${selectedDetections.length} Detection(s)`}
        message="Are you sure you want to delete the selected detection(s)? This action cannot be undone."
        loading={deleting}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DetectionSearch;
